import { DocumentNode, gql } from '@apollo/client';
import { Avatar, Box, Link, Stack, StackProps, Text } from '@chakra-ui/react';
import NextLink from 'next/link';
import * as React from 'react';

import { usePermissions } from '@/hooks/usePermissions';
import { getCompanyJobTitle } from '@/utilities';

import { PostHeader_PostFragment } from './graphql-types/PostHeader_PostFragment';

import { DropdownMenu } from '../DropdownMenu';
import { TimingAndOrigin } from './components/TimingAndOrigin';

const POST_HEADER = gql`
  fragment PostHeader_PostFragment on PostV2 {
    _id
    datePublished
    author {
      _id
      company
      jobTitle
      displayName
      proxyProfilePictureUrl(
        input: { w: 140, h: 140, fit: "facearea", facepad: 10.0 }
      )
    }
    group {
      _id
      name
    }
    organization {
      _id
      name
    }
    event {
      _id
      name
    }
    sponsorData {
      isSponsoredPost
    }
    ...DropdownMenu_PostFragment
    currentUserIsPostAuthor
    ... on SimplePost {
      scheduledPublishDate
    }
    ... on PollV2 {
      scheduledPublishDate
    }
    ... on QuestionV2 {
      scheduledPublishDate
    }
    ... on BlogV2 {
      scheduledPublishDate
    }
  }
  ${DropdownMenu.postFragment}
`;

type PostHeaderOptions = {
  showCommentedHeader?: boolean;
  showDraftedHeader?: boolean;
};

export const PostHeader: React.FC<
  {
    post: PostHeader_PostFragment;
    options?: PostHeaderOptions;
  } & StackProps
> & {
  postFragment: DocumentNode;
} = ({ post, options, ...stackProps }) => {
  const { canAccessCommunityAndGroupFeed } = usePermissions();

  const headerDate = (() => {
    if (post.datePublished) return new Date(post.datePublished);
    if (
      post.__typename === 'SimplePost' ||
      post.__typename === 'BlogV2' ||
      post.__typename === 'PollV2' ||
      post.__typename === 'QuestionV2'
    ) {
      if (post.scheduledPublishDate) return new Date(post.scheduledPublishDate);
    }
    return null;
  })();

  const isScheduledPost = (() => {
    if (!!post.datePublished) return false;
    if (
      post.__typename === 'SimplePost' ||
      post.__typename === 'BlogV2' ||
      post.__typename === 'PollV2' ||
      post.__typename === 'QuestionV2'
    ) {
      if (post.scheduledPublishDate) return true;
    }
    return false;
  })();

  return (
    <Stack direction="row" spacing="2" alignItems="center" {...stackProps}>
      <NextLink href={`/members/${post.author._id}`}>
        <Avatar
          size="md"
          name={post.author.displayName}
          src={post.author.proxyProfilePictureUrl || undefined}
        />
      </NextLink>
      <Box color="gray.600" flex="1">
        <Link as={NextLink} href={`/members/${post.author._id}`}>
          {post.author.displayName}
        </Link>
        <Text textStyle="compact" noOfLines={1}>
          {getCompanyJobTitle(post.author.company, post.author.jobTitle)}
        </Text>
        <TimingAndOrigin
          date={headerDate}
          group={post.group}
          organization={post.organization}
          event={post.event}
          isSponsoredPost={post.sponsorData?.isSponsoredPost}
          showCommentedHeader={options?.showCommentedHeader}
          showDraftedHeader={options?.showDraftedHeader}
          disableLinksToGroups={!canAccessCommunityAndGroupFeed}
          isScheduledPost={isScheduledPost}
        />
      </Box>
      <Box alignSelf="flex-start">
        <DropdownMenu post={post} aria-label="more options" alignSelf="start" />
      </Box>
    </Stack>
  );
};

PostHeader.postFragment = POST_HEADER;
